import { readAllStoredTokens, resolveOptionalConfig } from './auth';
import { version, readBrandEnv, specExtension } from './brand.js';
import { isSuperadminToken } from './jwt';

export function printVersion() {
  console.log(`canary v${version}`);
}

export function printHelp({
  isSuperadmin,
  features = {},
}: {
  isSuperadmin: boolean;
  features?: Record<string, boolean>;
}) {
  const lines: string[] = [
    `canary v${version}: Testing and QA CLI`,
    '',
    'Usage:',
    '  canary test-suite <sub-command>       Run and query test suite results',
    '  canary external-run <sub-command>     Diagnose external suite runs (no superadmin access)',
    '  canary mcp                              Start MCP server (stdio)',
    '  canary install [--target <id>]          Install MCP server into Claude, Cursor, etc.',
    '  canary login [--org <name>] [--app-url https://app.runcofactor.com] [--no-open]',
    '  canary orgs [members]                  List organizations + your logged-in sessions',
    '  canary org use <name|id>               Set the active org for bare commands',
    '  canary record [options]                 Record browser interactions',
    '  canary local <sub-command>             Set up and manage local testing',
    '  canary session <sub-command>           Manage local browser sessions',
    '  canary issues <sub-command>            Search and view issues',
    '  canary credential <sub-command>        List, create, update, and test credentials',
    '  canary persona <sub-command>           Manage per-application personas',
    '  canary workflow <sub-command>          Inspect workflow definitions',
    `  canary spec <sub-command>              Author workflows from ${specExtension} spec files`,
    '  canary api-catalog <sub-command>       Manage API catalog sources and endpoints',
    '  canary skill <name>                    Install an AI agent skill template (--name to rename)',
    '  canary prompts <list|get>              Curated starter prompts for coding agents',
  ];

  lines.push('  canary release <sub-command>            Release QA gate (CI/CD)');
  lines.push('  canary planning <sub-command>           Draft and inspect organization plans');

  lines.push(
    '  canary application <sub-command>         List and create applications, view linked repos (alias: property)',
    '  canary environment <sub-command>        List, create, and default environments (alias: env)',
    '  canary sandbox <sub-command>            Manage sandbox templates and instances',
    '  canary attachment <sub-command>          List, upload, and manage attachments',
    '  canary tag <sub-command>                 List, create, and assign tags',
    '  canary triage <sub-command>              Read AI triage results for a test suite execution',
    '  canary schedule suite <sub-command>      Schedule recurring test-suite runs',
    '  canary alert <sub-command>               Manage notify-only alerts (Slack/email)'
  );

  // Feature-gated agent commands — only listed when the signed-in org has the
  // backing feature flag (resolved from /me). Hidden otherwise so help never
  // advertises a command that returns 403.
  if (features.agentTasks) {
    lines.push('  canary task <sub-command>               Manage agent tasks');
  }
  if (features.utilities) {
    lines.push(
      '  canary utilities <group> <sub>          Object storage, gists, email/slack (agent toolkit)'
    );
  }

  if (isSuperadmin) {
    lines.push(
      '  canary debug-session [--env dev|local] [--json]  Create browser debug session',
      '  canary psql <query> [--json]            Execute read-only SQL',
      '  canary redis <command> [--json]         Execute read-only Redis commands',
      '  canary docs <sub-command>               Manage documentation pages',
      '  canary landing <sub-command>            Manage runcofactor.com/lp/<slug> landing pages',
      '  canary feature-flag <sub-command>       Manage feature flags',
      '  canary knobs <sub-command>              Manage knobs (global config)',
      '  canary product <sub-command>            Manage the Product board (cards, fields, flag links)',
      '  canary metrics <sub-command>            Manage product metrics (pull/review health metrics)',
      '  canary benchmark <sub-command>          Run/inspect ad-hoc A/B experiments (models, knobs, flags)',
      '  canary account <sub-command>            Manage the Accounts board (accounts, org links)',
      '  canary status <sub-command>             Publish incidents to the status page'
    );
  }

  lines.push(
    '  canary auth doctor [--env <env>]        Diagnose auth / corporate-proxy issues',
    '  canary upgrade                          Upgrade CLI to latest version',
    '  canary version                          Show version',
    '  canary help',
    '',
    'Test suite sub-commands:',
    '  run [options]                          Trigger a test suite and stream results',
    '  list [options]                         List recent test suites',
    '  get <id> [options]                     Show suite detail with runs',
    '  diagnostics <workflowId> <runId>       Get failure diagnostic',
    '  steps <workflowId> <runId>             Get workflow run steps',
    '',
    'Test suite run options:',
    '  --token <key>         API key (or set CANARY_API_TOKEN)',
    '  --api-url <url>       API URL (default: https://api.runcofactor.com)',
    '  --application <name|id>  Target a specific application (alias: --property)',
    '  --environment <name|id>  Target a specific environment',
    '  --tag <tag>           Filter workflows by tag',
    '  --name-pattern <pat>  Filter workflows by name pattern',
    '  --verbose, -v         Show all events',
    '',
    'Login options:',
    '  --org <name>          Select organization by name or ID (for multi-org users)',
    '',
    'Working across orgs (act on behalf of customers):',
    '  Stay logged into several orgs at once and pick which one any command runs as.',
    '  (--org goes AFTER the command, like --env: `canary <command> ... --org <name>`.)',
    '  canary <command> --org <name|id>   Run one command as one of your logged-in orgs',
    '  CANARY_ORG=<name|id>               Persistent default org (like AWS_PROFILE)',
    '  canary login --org <name>          Add / refresh a session (side by side per env)',
    '  canary org use <name>              Set the active org for bare (no --org) commands',
    '  canary orgs                        See your logged-in orgs, their env, token expiry',
    '  (Superadmin cross-org commands target a different org with --target-org.)',
    '',
    'Login environments:',
    '  Production:  canary login',
    '  Dev:         canary login --app-url https://app.dev.trycanary.ai --api-url https://api.dev.trycanary.ai',
    '  Local:       canary login --app-url http://localhost:5173 --api-url http://localhost:3000',
    '',
    '  Or set CANARY_API_URL env var for non-production environments:',
    '    export CANARY_API_URL=http://localhost:3000'
  );

  lines.push(
    '',
    'Debug options:',
    '  --to-step <N>         Stop after step N (1-based, default: all)',
    '  --env <env>           Environment (local, dev, prod)',
    '  --verbose, -v         Show all SSE events',
    '',
    'Issues sub-commands:',
    '  list [options]                         List and search issues',
    '  get <issueId> [options]                Get issue detail with diagnostics',
    '',
    'Issues options:',
    '  --search <query>      Full-text search',
    '  --severity <level>    Filter: low, medium, high, unknown',
    '  --status <statuses>   Filter: open, closed, not_a_bug (comma-separated)',
    '  --application-id <uuid>  Filter by application (alias: --property-id)',
    '  --page <n>            Page number (default: 1)',
    '  --page-size <n>       Page size (default: 25)',
    '  --json                Output raw JSON',
    '  --format markdown     Output as markdown',
    '',
    'Release sub-commands:',
    '  trigger --application-id <uuid>        Trigger a Release QA run (alias: --property-id)',
    '  status <run-id> [--json]               Check run status',
    '  run --application-id <uuid> [--timeout N] Trigger and poll until complete',
    '',
    'Record options:',
    '  --credential <id>    Credential ID (skip interactive selection)',
    '  --url <startUrl>     URL to navigate to after launch',
    '  --output <dir>       Local output directory (default: temp dir)',
    '  --env <env>          Environment (local, dev, prod)',
    '  --no-upload          Save locally only, skip API upload',
    ''
  );

  if (isSuperadmin) {
    lines.push(
      '',
      'PSQL options:',
      '  --json                Output results as JSON',
      '  --query <sql>         SQL query (alternative to positional)',
      '',
      'Redis options:',
      '  --json                Output results as JSON',
      '',
      'Feature flag sub-commands:',
      '  list                               List all flags',
      '  create <name> [--description <text>]  Create a flag',
      '  delete <name>                      Delete a flag and its gates',
      '  enable <name> --target-org <orgId>   Enable for an org',
      '  disable <name> --target-org <orgId>  Disable for an org',
      '  lifecycle <name> --stage <stage> [--final-value true|false]',
      '                                     Set lifecycle metadata',
      '',
      'Knobs sub-commands:',
      '  list                               List all knobs',
      '  get <key>                          Get a knob value',
      '  set <key> <value> --type <type>    Set a knob value',
      '  delete <key>                       Delete a knob',
      '  toggle <key>                       Toggle a boolean knob',
      '  lifecycle <key> --stage <stage> [--final-value <value>]',
      '                                     Set lifecycle metadata',
      '',
      'Product board sub-commands (org you are logged into):',
      '  list [--search <t>] [--priority p0,p1] [--include-deprecated]  List items',
      '  create <title> [--description <t>] [--stage <s>] [--priority <p0-p3>]  Create item',
      '  set <id> [--title <t>] [--description <t>] [--priority <p0-p3|null>]   Update fields',
      '  move <id> --stage <s> [--before <id>] [--after <id>]  Move card to a column',
      '  delete <id>                        Delete item',
      '  customer add <id> (--account-card <id> | --target-org <orgId>) [--name <t>]   Link customer',
      '  customer remove <id> <customerId>  Unlink customer',
      '  link-flag <cardId> --flag <name> | --flag <name> --clear  Link/unlink a feature flag',
      '  flags <cardId>                     List flags linked to a card',
      '',
      'Account board sub-commands (org you are logged into):',
      '  list [--search <t>] [--stage <s>]  List accounts',
      '  create <name> [--description <t>] [--stage <s>]  Create account (default stage=prospect)',
      '  set <id> [--title <t>] [--description <t>] [--stage <s>]  Update fields',
      '  delete <id>                        Delete account',
      '  link-org <id> --target-org <customerOrgId>  Bind account to a customer org',
      '  unlink-org <id>                    Remove org link',
      '  linked-org <id>                    Show linked org'
    );
  }

  lines.push('', 'Flags:', '  -h, --help     Show help', '  -V, --version  Show version');

  console.log(lines.join('\n'));
}

export function printTestHelp() {
  console.log(
    [
      `canary v${version}: Test command`,
      '',
      '\x1b[33m⚠ `canary test` is deprecated. Use `canary test-suite run` instead.\x1b[0m',
      '',
      'Usage:',
      '  canary test-suite run [options]         Trigger a test suite and stream results',
      '  canary test-suite list                  List recent test suites',
      '  canary test-suite get <id>              Show suite detail with runs',
      '',
      'Run options:',
      '  --token <key>          API key (or set CANARY_API_TOKEN)',
      '  --api-url <url>        API URL (default: https://api.runcofactor.com)',
      '  --application <name|id>  Target a specific application (alias: --property)',
      '  --environment <name|id>  Target a specific environment',
      '  --tag <tag>            Filter workflows by tag',
      '  --name-pattern <pat>   Filter workflows by name pattern',
      '  --verbose, -v          Show all events',
      '',
      'Examples:',
      '  canary test-suite run                   Run all workflow tests',
      '  canary test-suite run --tag smoke       Run smoke-tagged workflows',
      '  canary test-suite run --property "My App" --environment staging',
    ].join('\n')
  );
}

/** Check if any stored profile (or env token) has superadmin privileges */
export async function resolveIsSuperadmin(): Promise<boolean> {
  const envToken = readBrandEnv('CANARY_API_TOKEN');
  if (envToken) return isSuperadminToken(envToken);

  const tokens = await readAllStoredTokens();
  return tokens.some((t) => isSuperadminToken(t));
}

/**
 * Best-effort fetch of the signed-in org's enabled feature flags (the same
 * `user.features` map the web app uses), so the global help can hide commands
 * for features the org doesn't have. Fail-closed: any error, timeout, or
 * missing auth yields `{}`, which hides feature-gated commands rather than
 * advertising something the org can't use. Never throws.
 */
export async function resolveOrgFeatures(): Promise<Record<string, boolean>> {
  try {
    const config = await resolveOptionalConfig([]);
    if (!config) return {};
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 3000);
    try {
      const res = await fetch(`${config.apiUrl}/me`, {
        headers: { Authorization: `Bearer ${config.token}` },
        signal: controller.signal,
      });
      if (!res.ok) return {};
      const body = (await res.json()) as { user?: { features?: Record<string, boolean> } };
      return body.user?.features ?? {};
    } finally {
      clearTimeout(timer);
    }
  } catch {
    return {};
  }
}

/** Resolve superadmin + org features in parallel, then print the global help. */
export async function showGlobalHelp(): Promise<void> {
  const [isSuperadmin, features] = await Promise.all([resolveIsSuperadmin(), resolveOrgFeatures()]);
  printHelp({ isSuperadmin, features });
}
